import type { GetEventResponse } from '~entities/events/api/get-event';

import {
  calculateDurationInMinutes,
  normalizeTime,
} from '~features/classes/ui/helpers';

import { TuitionBillingCycle } from '~shared/constants/tuition-billing-cycle';
import { extractDateOnly } from '~shared/helpers/datetime';

import { DEFAULT_EVENT_AUTO_HIDE_GRACE_HOURS } from '../events.types';
import { isEventAutoHideGraceHours } from '../events.helpers';

import type { FormData } from './update-event-page.types';

const emptySession = { value: '', label: '' };

export const mapEventToFormData = (eventData: GetEventResponse): FormData => {
  const normalizedStartTime = normalizeTime(eventData.startTime ?? '');
  const normalizedEndTime = normalizeTime(eventData.endTime ?? '');

  return {
    days: eventData.days ?? [],
    eventInfo: {
      eventName: eventData.title ?? '',
      location: {
        label: eventData.location?.fieldName ?? null,
        value: eventData.location?._id ?? null,
      },
      room: {
        label: eventData.room?.fieldName ?? null,
        value: eventData.room?._id ?? null,
      },
      instructor:
        eventData.instructor?.map((instructor) => ({
          label: instructor.fieldName,
          value: instructor._id,
        })) ?? [],
      seats: eventData.maxSize || 0,
      minAge: {
        year: eventData.startYear || 0,
        month: eventData.startMonth || 0,
      },
      maxAge: {
        year: eventData.endYear || 0,
        month: eventData.endMonth || 0,
      },
      session: eventData.session
        ? {
            value: eventData.session._id ?? '',
            label: eventData.session.name ?? '',
          }
        : emptySession,
      hide: eventData.hide ?? false,
      autoHideAfterEnd: eventData.autoHideAfterEnd !== false,
      autoHideGraceHours: isEventAutoHideGraceHours(
        eventData.autoHideGraceHours ?? DEFAULT_EVENT_AUTO_HIDE_GRACE_HOURS,
      )
        ? eventData.autoHideGraceHours ?? DEFAULT_EVENT_AUTO_HIDE_GRACE_HOURS
        : DEFAULT_EVENT_AUTO_HIDE_GRACE_HOURS,
      allowSpecificDatesSelection:
        eventData.allowSpecificDatesSelection === true,
      includeInPublicSchedule: eventData.includeInPublicSchedule ?? false,
      color: eventData.color ?? '',
    },
    dates: {
      startDate: extractDateOnly(eventData.startDate) ?? '',
      endDate: extractDateOnly(eventData.endDate) ?? '',
      registrationStartDate:
        extractDateOnly(eventData.registrationStartDate ?? '') ?? '',
    },
    times: {
      startTime: normalizedStartTime,
      endTime: normalizedEndTime,
      duration:
        eventData.duration ??
        calculateDurationInMinutes(normalizedStartTime, normalizedEndTime),
    },
    otherInfo: {
      registrationFee: eventData.registrationFeeAmount?.toString() ?? '',
      costumeFee: eventData.costumeFee?.toString() ?? '',
      tags:
        eventData.tags?.map((tag) => ({
          label: (tag as unknown as { fieldName: string; _id: string })
            .fieldName,
          value: tag._id,
        })) ?? [],
      policyGroup:
        eventData.policyGroup?.map((policy) => ({
          label: policy.name,
          value: policy._id,
        })) ?? [],
      group:
        eventData.group?.map((group) => ({
          label: (group as unknown as { fieldName: string; _id: string })
            .fieldName,
          value: group._id,
        })) ?? [],
      description: eventData.description ?? '',
      tuitionBillingCycle:
        eventData.tuitionBillingCycle || TuitionBillingCycle.ONE_TIME,
      tuitionFee: eventData.tuitionFee != null ? String(eventData.tuitionFee) : '',
      billingDay: eventData.billingDay || 1,
      billingFrequencyDays: eventData.billingFrequencyDays || undefined,
      prorateTuition: eventData.prorateTuition ?? true,
    },
    timeConfig: (eventData.timeConfig as 'same' | 'different') ?? 'same',
    dayTimes: Object.fromEntries(
      (eventData.availability ?? []).map(({ day, startTime, endTime }) => [
        day,
        {
          startTime: normalizeTime(startTime ?? '') ?? '',
          endTime: normalizeTime(endTime ?? '') ?? '',
        },
      ]),
    ),
  };
};
